import { etiquetasCortas, mensajesOf, modulesOf, type Snapshot } from '../lib/snapshot-types.js';
import { BarsByRep, type BarSeries } from './BarsByRep.js';
import { navigate } from '../lib/hash-route.js';
import { etiquetaMotivo, etiquetaResultado, eurosCompacto } from '../lib/etiquetas-metricas.js';

interface Props {
  snapshot: Snapshot;
  /** Solo para los enlaces de la forma vieja (#/emails-list?scope=...). */
  scope?: 'me' | 'team';
}

/**
 * Column classes for a row of cards. The base .cards-grid is a fixed 3-column
 * grid; a row with fewer cards (the tenant did not contract them) gets a
 * modifier so the cards fill the width instead of leaving a hole.
 */
function filas<T>(cards: T[]): T[][] {
  if (cards.length === 4) return [cards.slice(0, 2), cards.slice(2)];
  const out: T[][] = [];
  for (let i = 0; i < cards.length; i += 3) out.push(cards.slice(i, i + 3));
  return out;
}
function gridClass(cards: number): string {
  return cards >= 3 ? 'cards-grid' : `cards-grid cards-grid--${cards}`;
}

const DISP_COLORS: Record<string, string> = {
  'Demo Scheduled':       '#16a34a',
  'Connected Positive':   '#22c55e',
  'Connected Negative':   '#dc2626',
  'Connected Busy':       '#f59e0b',
  'No Answer':            '#9ca3af',
  'Busy':                 '#6b7280',
  'Wrong Number':         '#a855f7',
  'voicemail':            '#9ca3af',
  'no_answer':            '#9ca3af',
  'connected_positive':   '#22c55e',
  'connected_negative':   '#dc2626',
  'meeting_booked':       '#16a34a',
  'call_back_later':      '#f59e0b',
  'wrong_number':         '#a855f7',
  'busy':                 '#6b7280',
  'unknown':              '#d1d5db',
};

/** Quien se ve: la seleccion del filtro, para la barra que no se reparte por persona. */
function quienSeVe(s: Snapshot): string {
  const f = s.filtro?.personas;
  if (!f || f === 'todas') return 'Todas';
  if (f.length === 1) return s.personas?.find((p) => p.id === f[0])?.nombre ?? 'Elegidas';
  return `${f.length} personas`;
}

export function MetricsGrid({ snapshot, scope = 'team' }: Props) {
  const modules = modulesOf(snapshot);
  const correos = mensajesOf(snapshot, 'emails');
  const whatsapp = mensajesOf(snapshot, 'whatsapp');
  const todos = [
    ...(snapshot.calls.por_persona ?? []).map((p) => p.persona),
    ...correos.por_persona.map((p) => p.persona),
    ...whatsapp.por_persona.map((p) => p.persona),
  ];
  const corta = etiquetasCortas([...new Set(todos)]);
  const etq = (n: string): string => corta.get(n) ?? n;

  // ── LLAMADAS: por persona, apiladas por resultado ──
  // `label` es el resultado tal cual (filtra la lista al pulsar); `display`, lo que se lee.
  const segmento = (disposition: string, value: number) => ({
    label: disposition, display: etiquetaResultado(disposition), value, color: DISP_COLORS[disposition] ?? '#9ca3af',
  });
  const callsBars: BarSeries[] = snapshot.calls.total_calls === 0 ? [] : snapshot.calls.por_persona
    ? snapshot.calls.por_persona.map((p) => ({ rep: etq(p.persona), total: p.total, segments: p.por_resultado.map((d) => segmento(d.disposition, d.count)) }))
    : [{ rep: quienSeVe(snapshot), total: snapshot.calls.total_calls, segments: snapshot.calls.by_disposition.map((d) => segmento(d.disposition, d.count)) }];

  // ── CORREOS / WHATSAPP: enviados por persona (la tasa es por contacto, va arriba) ──
  const barrasDe = (m: typeof correos, color: string): BarSeries[] =>
    m.por_persona.filter((p) => p.enviados > 0).map((p) => ({ rep: etq(p.persona), total: p.enviados, segments: [{ label: 'enviados', display: 'Enviados', value: p.enviados, color }] }));
  const emailsBars = barrasDe(correos, '#2563eb');
  const waBars = barrasDe(whatsapp, '#25D366');

  // ── OPORTUNIDADES GANADAS (solo con el modulo) ──
  const wonCount = snapshot.opportunities.summary.won ?? snapshot.opportunities.summary.won_quarter;
  // El importe ganado vendria del CRM externo y no declara moneda: guion, nunca "0 €".
  const wonValue = snapshot.revenue_progress.status === 'ok' ? (snapshot.revenue_progress.org?.achieved_quarter ?? null) : null;
  const delPeriodo = snapshot.filtro ? 'periodo' : 'trimestre';

  const tarjetas: Array<{ clave: string; nodo: React.ReactNode }> = [];
  // Por persona: el reparto. El total y la tasa ya estan en la fila resumen de arriba: aqui no se
  // repiten (revision del 7-oct). La cifra de cada persona va encima de su barra.
  if (modules.calls) tarjetas.push({ clave: 'llamadas', nodo: (
    <div className="card" key="llamadas">
      <div className="card-header">
        <div>
          <div className="card-title">Llamadas</div>
          <div className="card-rate-label">por resultado</div>
        </div>
      </div>
      <div className="chart-slot" style={{ marginTop: 4 }}>
        {callsBars.length > 0
          ? <BarsByRep data={callsBars} height={180} grouped={false} onSegmentClick={(_rep, label) => navigate({ kind: 'calls-list', disposition: label })} />
          : <Empty msg="Sin llamadas en este periodo." />}
      </div>
      <div className="card-footer">
        <strong>{snapshot.calls.connected_min}</strong> min de conversación
      </div>
    </div>
  ) });

  const tarjetaMensajes = (clave: 'correos' | 'whatsapp', titulo: string, m: typeof correos, barras: BarSeries[], motivo: string | undefined) => (
    <div className="card" key={clave}>
      <div className="card-header">
        <div>
          <div className="card-title">{titulo}</div>
          <div className="card-rate-label">enviados</div>
        </div>
      </div>
      <div className="chart-slot" style={{ marginTop: 4 }}>
        {barras.length > 0
          ? <BarsByRep data={barras} height={180} grouped={false} showLegend={false} onSegmentClick={() => navigate({ kind: clave === 'correos' ? 'emails-list' : 'whatsapp-list', scope, filter: 'all' })} />
          : <Empty msg={motivo ? etiquetaMotivo(motivo) : `Sin ${clave === 'correos' ? 'correos enviados' : 'WhatsApp enviados'} en este periodo.`} />}
      </div>
      <div className="card-footer">
        {m.personas !== null && m.respondieron !== null && m.respondieron > 0
          ? <a href={clave === 'correos' ? `#/emails-list?scope=${scope}&filter=replied` : `#/whatsapp-list?scope=${scope}&filter=replied`}>Ver quién respondió</a>
          : <span>Nadie ha respondido en este periodo</span>}
        {m.sin_verificar > 0 && <> · {m.sin_verificar} sin verificar</>}
      </div>
    </div>
  );
  tarjetas.push({ clave: 'correos', nodo: tarjetaMensajes('correos', 'Correos', correos, emailsBars, snapshot.emails.status === 'ok' ? undefined : snapshot.emails.reason) });
  tarjetas.push({ clave: 'whatsapp', nodo: tarjetaMensajes('whatsapp', 'WhatsApp', whatsapp, waBars, snapshot.whatsapp.status === 'ok' ? undefined : snapshot.whatsapp.reason) });

  if (modules.ganadas) tarjetas.push({ clave: 'ganadas', nodo: (
    <div className="card" key="ganadas">
      <div className="card-header">
        <div>
          <div className="card-title">Oportunidades ganadas</div>
          <div className="card-rate-label">{wonCount} oportunidades · {delPeriodo}</div>
        </div>
        <div className="card-rate">{wonValue === null ? '-' : eurosCompacto(wonValue)}</div>
      </div>
      <div className="chart-slot" style={{ marginTop: 4 }}>
        {wonCount > 0
          ? <BarsByRep data={[{ rep: quienSeVe(snapshot), total: wonCount, segments: [{ label: 'ganadas', display: 'Ganadas', value: wonCount, color: '#16a34a' }] }]} height={180} grouped={false} onSegmentClick={() => navigate({ kind: 'won-deals', scope })} />
          : <Empty msg="Sin oportunidades ganadas en este periodo." />}
      </div>
      <div className="card-footer">
        <strong>{snapshot.opportunities.summary.open}</strong> abiertas · {eurosCompacto(snapshot.opportunities.summary.total_open_value)} en el embudo
      </div>
    </div>
  ) });

  // «Automatizaciones» (los workflows publicados en GHL) se quito el 16-oct: no decia nada de la venta.

  return (
    <>
      {filas(tarjetas).map((fila, i) => (
        <section key={i} className={gridClass(fila.length)}>{fila.map((t) => t.nodo)}</section>
      ))}
    </>
  );
}

function Empty({ msg }: { msg: string }) {
  return <div style={{ fontSize: 12, color: 'var(--fg-subtle)', padding: 20, textAlign: 'center' }}>{msg}</div>;
}
